import React, { useState, useEffect, useCallback, useRef } from 'react';
import comms from '../../comms';

const formatTimeHHMM = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date)) {
    return "00:00";
  }
  const hours = date.getHours().toString().padStart(2, '0');
 const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};



const FixedCurrentWaypoint = ({ 
  waypoint, 
  expectedTime, 
  isAccurate, 
  isPhone, 
  vehiclePosition = null,
  currentLocation = null,
  currentRun = null, // Optional fallback for run information
  fixedRouteData = null, // Optional fallback for route data
  userSettings,
  selectedVehicle,
  routeDetails,
  breakLogs = [], // Server-authoritative break logs
  endPointLog = null, // Server-authoritative endpoint log
  waypointArrived = false, // Server confirmation of arrival
  onStopCompleted
}) => {
  // Stop status: 'arriving', 'passengersOut', 'passengersIn'
  const [stopStatus, setStopStatus] = useState('arriving');
  const [passengersOut, setPassengersOut] = useState('');
  const [passengersIn, setPassengersIn] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [breakTimeRemaining, setBreakTimeRemaining] = useState(null);
  const [canDepart, setCanDepart] = useState(true);
  const inputRef = useRef(null);
  const prevWaypointArrived = useRef(waypointArrived);

  // Helper function to get run information with fallbacks
  const getRunInfo = useCallback(() => {
    // First try: routeDetails (from authorization response routeObject)
    if (routeDetails?.runId && routeDetails?.runName) {
      return {
        runName: routeDetails.runName || '',
        runId: routeDetails.runId || ''
      };
    }
    
    // Second try: waypoint.run (should be available after socketHelper enhancement)
    if (waypoint?.run) {
      return {
        runName: waypoint.run.Name || '',
        runId: waypoint.run.Id || ''
      };
    }
    
    // Third try: currentRun prop
    if (currentRun) {
      return {
        runName: currentRun.Name || '',
        runId: currentRun.Id || ''
      };
    }
    
    // Fourth try: fixedRouteData
    if (fixedRouteData?.Runs && Array.isArray(fixedRouteData.Runs) && fixedRouteData.Runs.length > 0) {
      const firstRun = fixedRouteData.Runs[0];
      return {
        runName: firstRun.Name || '',
        runId: firstRun.Id || ''
      };
    }
    
    // Fallback: empty values
    console.warn('No run information available for fixed current waypoint');
    return {
      runName: '',
      runId: ''
    };
  }, [waypoint, currentRun, fixedRouteData, routeDetails]);

  // Get active break for current waypoint using breakStep flag
  const getCurrentWaypointBreak = useCallback(() => {
    // Use waypoint.breakStep flag to determine if this is a break stop
    if (!waypoint?.breakStep || !breakLogs || breakLogs.length === 0) return null;
    
    // Use consistent ID property (server ensures waypoint.Id is standardized)
    const waypointId = waypoint.Id || waypoint.StopId || waypoint.ItineraryId;
    if (!waypointId) return null;
    
    // Find break log for current waypoint - should be the first (most recent) one
    return breakLogs.find(log => 
      log.StopId === waypointId || log.FixedRoute_StopId === waypointId
    ) || null;
  }, [waypoint, breakLogs]);

  // Check if current waypoint is an endpoint using endStep flag
  const isCurrentWaypointEndpoint = useCallback(() => {
    // Use waypoint.endStep flag to determine if this is an endpoint
    return waypoint?.endStep === true;
  }, [waypoint]);

  // Helper function to parse various time formats to Date object
  const parseTimeToDate = useCallback((timeValue) => {
    if (!timeValue) return null;
    
    // If it's already a Date object
    if (timeValue instanceof Date) return timeValue;
    
    // If it's an ISO string (like "2024-01-01T14:30:00")
    if (typeof timeValue === 'string' && timeValue.includes('T')) {
      return new Date(timeValue);
    }
    
    // If it's a time string (like "14:30" or "2:30 PM")
    if (typeof timeValue === 'string') {
      const today = new Date();
      const [hours, minutes] = timeValue.includes(':') ? 
        timeValue.split(':').map(Number) : [0, 0];
      
      if (!isNaN(hours) && !isNaN(minutes)) {
        today.setHours(hours, minutes, 0, 0);
        return today;
      }
    }
    
    return null;
  }, []);

  // Calculate break time remaining using server break data
  const checkBreakTimeRemaining = useCallback(() => {
    // Only calculate break time if this is a break step
    if (!waypoint?.breakStep) return null;
    
    // First try to use waypoint.breakDepartureTime directly from the server
    if (waypoint.breakDepartureTime) {
      const breakEndTime = parseTimeToDate(waypoint.breakDepartureTime);
      if (breakEndTime) {
        const now = new Date();
        const timeDiff = breakEndTime.getTime() - now.getTime();
        return Math.max(0, Math.ceil(timeDiff / 1000));
      }
    }
    
    // Fallback to break logs if waypoint.breakDepartureTime is not available
    const currentBreak = getCurrentWaypointBreak();
    if (!currentBreak || !currentBreak.DepartureTime) return null;
    
    // Parse the DepartureTime from the break log
    const breakEndTime = parseTimeToDate(currentBreak.DepartureTime);
    if (!breakEndTime) return null;
    
    const now = new Date();
    const timeDiff = breakEndTime.getTime() - now.getTime();
    
    // Return seconds remaining (or 0 if break time has passed)
    return Math.max(0, Math.ceil(timeDiff / 1000));
  }, [waypoint, getCurrentWaypointBreak, parseTimeToDate]);

  // Handle endpoint completion - inspection is now handled by CompleteShift component
  const handleEndpointCompletion = useCallback(() => {
    console.log('Fixed route endpoint reached - sending FIXED_ROUTE_END message');
    
    // Get userSettings from localStorage if not available
    let currentUserSettings = userSettings;
    if ((!currentUserSettings || Object.keys(currentUserSettings).length === 0) && typeof window !== 'undefined') {
      const storedSettings = localStorage.getItem('userSettings');  
      if (storedSettings) {
        try {
          const parsedSettings = JSON.parse(storedSettings);
          if (parsedSettings && typeof parsedSettings === 'object') {
            currentUserSettings = parsedSettings;
          }
        } catch (error) {
          console.error('Error parsing userSettings from localStorage:', error);
        }
      }
    }
    
    // Send FIXED_ROUTE_END message to server (similar to LOOPED_ROUTE_END)
    // This ensures route assignment is cleared when routeSelection is enabled
    if (waypoint) {
      const stopId = waypoint.Id || waypoint.StopId || waypoint.ItineraryId;
      const driverId = currentUserSettings?.Id || null;
      const vehicleId = selectedVehicle?.Id || selectedVehicle?.id || currentUserSettings?.VehicleID || null;
      const { runName, runId } = getRunInfo();
      const scheduleId = waypoint.ScheduleId || waypoint.scheduleId || routeDetails?.scheduleId || null;

      console.log(`Sending FIXED_ROUTE_END message: StopId=${stopId}, RunName=${runName}, RunId=${runId}`);
      
      const messageData = {
        stopId: stopId,
        runName: runName,
        runId: runId,
        driverId: driverId,
        vehicleId: vehicleId,
        itineraryId: waypoint?.ItineraryId || waypoint?.itineraryId || null,
        scheduleId: scheduleId,
        routeId: routeDetails?.routeId || null,
        completedAt: new Date().toISOString()
      };

      const message = `FIXED_ROUTE_END|${JSON.stringify(messageData)}`;
      comms.sendSocket(message);
      console.log(`Sent fixed route end message: ${message}`);
    }
    
    // Simply notify the parent component that the endpoint has been reached
    // The CompleteShift component will handle all inspection and completion logic
    if (onStopCompleted) {
      onStopCompleted(waypoint, 0);
    }
  }, [onStopCompleted, waypoint, userSettings, selectedVehicle, routeDetails, getRunInfo]);



  // Update time-based states every 10 seconds
  useEffect(() => {
    const updateTimeStates = () => {
      const isEndpoint = isCurrentWaypointEndpoint();
      const isBreakStep = waypoint?.breakStep === true;
      const isEndStep = waypoint?.endStep === true;
      
      // Check break time remaining (only for break steps)
      const breakRemaining = isBreakStep ? checkBreakTimeRemaining() : null;
      setBreakTimeRemaining(breakRemaining);
      
      // Update canDepart based on step type and conditions
      if (isBreakStep && breakRemaining !== null && breakRemaining > 0) {
        // Break step: cannot depart until break time has elapsed
        setCanDepart(false);
      } else if (isEndStep) {
        // End step: departure completes route and directs to inspection/lot return
        setCanDepart(true);
      } else {
        // Normal step: departure allowed
        setCanDepart(true);
      }
      
      console.log('Fixed route time check:', {
        currentTime: new Date().toLocaleTimeString(),
        stepType: isBreakStep ? 'break' : isEndStep ? 'end' : waypoint?.beginStep ? 'begin' : 'normal',
        breakRemaining,
        isEndpoint,
        canDepart: !(isBreakStep && breakRemaining > 0),
        waypoint: waypoint?.Name
      });
    };

    // Initial check
    updateTimeStates();
    
    // Set up 10-second interval
    const interval = setInterval(updateTimeStates, 10000);
    
    return () => clearInterval(interval);
  }, [waypoint, stopStatus, getCurrentWaypointBreak, isCurrentWaypointEndpoint, checkBreakTimeRemaining]);

  // Focus input when entering passenger entry phases
  useEffect(() => {
    if ((stopStatus === 'passengersOut' || stopStatus === 'passengersIn') && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 100);
    }
  }, [stopStatus]);

  // Update stop status based on waypointArrived prop
  useEffect(() => {
    if (waypointArrived) {
      // When arrived, go to passengers out phase
      setStopStatus('passengersOut');
      console.log('Updated stop status to passengersOut based on waypointArrived prop');
    } else {
      setStopStatus('arriving');
      console.log('Updated stop status to arriving based on waypointArrived prop');
    }
  }, [waypointArrived]);

  // Handle departure confirmation and completion notification
  useEffect(() => {
    // If waypointArrived goes from true to false, it means departure was confirmed
    if (prevWaypointArrived.current === true && waypointArrived === false) {
      console.log('Departure confirmed - resetting UI for next stop');
      setIsProcessing(false);
      setPassengersOut('');
      setPassengersIn('');
      
      // Notify parent component that this stop is completed
      if (onStopCompleted) {
        onStopCompleted(waypoint, 0);
      }
    }
    
    // Update previous state reference
    prevWaypointArrived.current = waypointArrived;
  }, [waypointArrived, onStopCompleted, waypoint]);

  if (!waypoint) return <h2>No current stop available</h2>;

  // Send departure message to server
  const sendDepartureMessage = () => {
    // Use consistent ID property (server ensures waypoint.Id is standardized)
    const stopId = waypoint.Id || waypoint.StopId || waypoint.ItineraryId;
    if (!stopId) {
      console.error('No valid stop ID found for waypoint:', waypoint);
      return;
    }
    
    // Get userSettings from localStorage if not available
    let currentUserSettings = userSettings;
    if ((!currentUserSettings || Object.keys(currentUserSettings).length === 0) && typeof window !== 'undefined') {
      const storedSettings = localStorage.getItem('userSettings');  
      if (storedSettings) {
        try {
          const parsedSettings = JSON.parse(storedSettings);
          if (parsedSettings && typeof parsedSettings === 'object') {
            currentUserSettings = parsedSettings;
          }
        } catch (error) {
          console.error('Error parsing userSettings from localStorage:', error);
        }
      }
    }
    
    const driverId = currentUserSettings?.Id || null;
    const vehicleId = selectedVehicle?.Id || selectedVehicle?.id || currentUserSettings?.VehicleID || null;
    const { runName, runId } = getRunInfo();

    // Get scheduleId from various possible sources
    const scheduleId = waypoint.ScheduleId || 
                      waypoint.scheduleId || 
                      routeDetails?.scheduleId ||
                      routeDetails?.schedule?.Id ||
                      currentRun?.scheduleId ||
                      null;

    const paxOut = passengersOut === '' ? 0 : parseInt(passengersOut);
    const paxIn = passengersIn === '' ? 0 : parseInt(passengersIn);

    console.log(`Sending DEPART message: StopId=${stopId}, RunName=${runName}, RunId=${runId}, PAXAlighted=${paxOut}, PAXBoarded=${paxIn}, ScheduleId=${scheduleId}`);
    
    const messageData = {
      stopId: stopId,
      itineraryId: waypoint?.ItineraryId || waypoint?.itineraryId || null,
      passengerCount: paxIn,  // PAXBoarded
      passengersAlighted: paxOut,  // PAXAlighted
      runName: runName,
      runId: runId,
      scheduleId: scheduleId,
      driverId: driverId,
      vehicleId: vehicleId,
      routeId: routeDetails?.routeId || null
    };

    const message = `FIXEDSTOP_DEPART|${JSON.stringify(messageData)}`;
    comms.sendSocket(message);
    console.log(`Sent fixed route departure message: ${message}`);
    
    // Waiting for server confirmation via parent component props
    console.log('Sent DEPART message - waiting for server confirmation via props');
    
    // Set timeout to prevent getting stuck if server doesn't respond
    setTimeout(() => {
      setIsProcessing(false);
      console.warn('Server confirmation timeout for DEPART action');
    }, 3000); // 10 second timeout
  };

  const handleArriveAtStop = async () => {
    if (isProcessing) return;
    
    // Get userSettings from localStorage if not available
    let currentUserSettings = userSettings;
    if ((!currentUserSettings || Object.keys(currentUserSettings).length === 0) && typeof window !== 'undefined') {
      const storedSettings = localStorage.getItem('userSettings');  
      if (storedSettings) {
        try {
          const parsedSettings = JSON.parse(storedSettings);
          if (parsedSettings && typeof parsedSettings === 'object') {
            currentUserSettings = parsedSettings;
            console.log('Dispatch: Loaded userSettings from localStorage:', currentUserSettings);
          }
        } catch (error) {
          console.error('Dispatch: Error parsing userSettings from localStorage:', error);
        }
      }
    }
    setIsProcessing(true);
    
    try {
        // Send arrival message for fixed route using the expected format
        if (waypoint) {
          // Use consistent ID property (server ensures waypoint.Id is standardized)
          const stopId = waypoint.Id || waypoint.StopId || waypoint.ItineraryId;
          if (!stopId) {
            console.error('No valid stop ID found for waypoint:', waypoint);
            return;
          }
        const driverId = currentUserSettings?.Id || null;
        const vehicleId = selectedVehicle?.Id || selectedVehicle?.id || currentUserSettings?.VehicleID || null;
          const { runName, runId } = getRunInfo();

          // Get scheduleId from various possible sources
          const scheduleId = waypoint.scheduleId || waypoint.ScheduleId;

          console.log(`Sending ARRIVE message: StopId=${stopId}, RunName=${runName}, RunId=${runId}, ScheduleId=${scheduleId}`);
          // Send message in the format expected by the sendSocket function: command|data
          const messageData = {
            stopId: stopId,
            itineraryId: waypoint?.ItineraryId || waypoint?.itineraryId || null,
            passengerCount: 0,
            runName: runName,
            runId: runId,
            scheduleId: scheduleId,
            driverId: driverId,
            vehicleId: vehicleId,
            routeId: routeDetails?.routeId || null
          };
          const message = `FIXEDSTOP_ARRIVE|${JSON.stringify(messageData)}`;
          comms.sendSocket(message);
          console.log(`Sent fixed route arrival message: ${message}`);
          
          // Waiting for server confirmation via parent component props
          console.log('Sent ARRIVE message - waiting for server confirmation via props');
          
          // Set timeout to prevent getting stuck if server doesn't respond
          setTimeout(() => {
            setIsProcessing(false);
            console.warn('Server confirmation timeout for ARRIVE action');
          }, 3000); // 10 second timeout
        }
    } catch (error) {
      console.error('Error processing fixed route arrival:', error);
      alert('Failed to process action. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleNextToPassengersIn = () => {
    setStopStatus('passengersIn');
  };

  const handleBackToPassengersOut = () => {
    setStopStatus('passengersOut');
  };

  const handleDepart = () => {
        // Check step type using waypoint flags
        const isEndStep = waypoint?.endStep === true;
        const isBreakStep = waypoint?.breakStep === true;
        
        if (isEndStep) {
          console.log('End step reached - directing driver to complete route and return to lot');
          handleEndpointCompletion();
          return;
        }
        
        // Check if break time is active for break steps
        if (isBreakStep && breakTimeRemaining > 0) {
          console.log(`Cannot depart from break step - break time remaining: ${breakTimeRemaining} seconds`);
          alert(`Please wait ${Math.ceil(breakTimeRemaining / 60)} more minute(s) before departing from break.`);
          return;
        }
        
    setIsProcessing(true);
    sendDepartureMessage();
  };

  // Increment/decrement helpers for touch-friendly buttons
  const incrementValue = (setter, currentValue) => {
    const current = currentValue === '' ? 0 : parseInt(currentValue);
    if (current < 99) {
      setter(String(current + 1));
    }
  };

  const decrementValue = (setter, currentValue) => {
    const current = currentValue === '' ? 0 : parseInt(currentValue);
    if (current > 0) {
      setter(String(current - 1));
    }
  };

  const handlePassengersOutChange = (e) => {
    const value = e.target.value;
    if (value === '' || (parseInt(value) >= 0 && parseInt(value) <= 99)) {
      setPassengersOut(value);
    }
  };

  const handlePassengersInChange = (e) => {
    const value = e.target.value;
    if (value === '' || (parseInt(value) >= 0 && parseInt(value) <= 99)) {
      setPassengersIn(value);
    }
  };

  return (
    <div className={`bg-white border rounded-lg shadow-sm h-full flex flex-col ${isPhone ? 'p-1' : 'p-2'}`}>
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <h2 className="text-2xl font-bold text-blue-600">
            {waypoint.Name || waypoint.LandmarkName || waypoint.StopName}
          </h2>
        </div>

        <div className="text-right">
          {/* Expected time display */}
          {waypoint.eta && !waypoint?.breakStep && !waypoint?.endStep && (
            <div className="mb-2">
              <div className="text-base text-gray-600">Expected</div>
              <div className="text-xl font-semibold text-green-600">
                {(new Date(waypoint.eta).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))}
              </div>
            </div>
          )}
         
          {/* Show break information for break steps */}
          {waypoint?.breakStep && (
            <div>
              <div className="text-base text-orange-600">Break Until</div>
              <div className="text-xl font-semibold text-orange-600">
                {(() => {
                  // First try waypoint.breakDepartureTime from server
                  if (waypoint.breakDepartureTime) {
                    const breakEnd = parseTimeToDate(waypoint.breakDepartureTime);
                    return breakEnd ? formatTimeHHMM(breakEnd) : "Break Active";
                  }
                  
                  // Fallback to break logs
                  const currentBreak = getCurrentWaypointBreak();
                  if (currentBreak?.DepartureTime) {
                    const breakEnd = parseTimeToDate(currentBreak.DepartureTime);
                    return breakEnd ? formatTimeHHMM(breakEnd) : "Break Active";
                  }
                  
                  return "Break Active";
                })()}
              </div>
              {breakTimeRemaining !== null && breakTimeRemaining > 0 && (
                <div className="text-base text-orange-500 mt-1">
                  {Math.ceil(breakTimeRemaining / 60)} min remaining
                </div>
              )}
            </div>
          )}
          
          {/* Route end display */}
          {waypoint?.endStep && (
            <div>
              <div className="text-base text-red-600">Route End</div>
              <div className="text-xl font-semibold text-red-600">
                Return to Lot
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Arriving State - Show Arrive Button */}
        {stopStatus === 'arriving' && (
          <div className="mt-auto">
            <button
              onClick={handleArriveAtStop}
              disabled={isProcessing || !isAccurate}
              className={`w-full py-3 px-4 rounded-lg text-xl font-semibold text-white transition-all duration-200 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isProcessing ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </div>
              ) : (
                'Arrive at Stop'
              )}
            </button>

        {!isAccurate && (
              <div className="text-sm text-red-600 text-center mt-1">
                GPS accuracy required
              </div>
            )}
          </div>
        )}

        {/* Passengers Out Phase */}
        {stopStatus === 'passengersOut' && (
          <div className="mt-auto">
            <div className="text-center mb-2">
              <span className="text-lg font-bold text-red-600">Passengers OUT</span>
            </div>
            
            <div className="flex items-center justify-center space-x-3 mb-2">
              <button
                onClick={() => decrementValue(setPassengersOut, passengersOut)}
                className="w-12 h-12 rounded-full bg-gray-100 text-gray-700 text-2xl font-bold hover:bg-gray-200 transition-colors flex items-center justify-center border border-gray-300"
                type="button"
              >
                −
              </button>
              
              <input
                ref={inputRef}
                type="number"
                min="0"
                max="99"
                value={passengersOut}
                onChange={handlePassengersOutChange}
                placeholder="0"
                className="w-20 h-12 text-center text-2xl font-bold border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              
              <button
                onClick={() => incrementValue(setPassengersOut, passengersOut)}
                className="w-12 h-12 rounded-full bg-gray-100 text-gray-700 text-2xl font-bold hover:bg-gray-200 transition-colors flex items-center justify-center border border-gray-300"
                type="button"
              >
                +
              </button>
            </div>
            <div className="text-base flex justify-center space-x-4 mb-2">
              <span><span className="text-gray-500">&nbsp;</span></span>
            </div>
            <button
              onClick={handleNextToPassengersIn}
              className="w-full py-2 px-4 rounded-lg text-lg font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              Next →
            </button>
          </div>
        )}

        {/* Passengers In Phase */}
        {stopStatus === 'passengersIn' && (
          <div className="mt-auto">
            <div className="text-center mb-2">
              <span className="text-lg font-bold text-green-600">Passengers IN</span>
            </div>
            
            <div className="flex items-center justify-center space-x-3 mb-2">
              <button
                onClick={() => decrementValue(setPassengersIn, passengersIn)}
                className="w-12 h-12 rounded-full bg-gray-100 text-gray-700 text-2xl font-bold hover:bg-gray-200 transition-colors flex items-center justify-center border border-gray-300"
                type="button"
              >
                −
              </button>
              
              <input
                ref={inputRef}
                type="number"
                min="0"
                max="99"
                value={passengersIn}
                onChange={handlePassengersInChange}
                placeholder="0"
                className="w-20 h-12 text-center text-2xl font-bold border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              
              <button
                onClick={() => incrementValue(setPassengersIn, passengersIn)}
                className="w-12 h-12 rounded-full bg-gray-100 text-gray-700 text-2xl font-bold hover:bg-gray-200 transition-colors flex items-center justify-center border border-gray-300"
                type="button"
              >
                +
              </button>
            </div>

            {/* Summary */}
            <div className="text-base flex justify-center space-x-4 mb-2">
              <span><span className="text-gray-500">Out:</span> <span className="font-semibold text-red-600">{passengersOut || '0'}</span></span>
              <span><span className="text-gray-500">In:</span> <span className="font-semibold text-green-600">{passengersIn || '0'}</span></span>
            </div>

            <div className="flex space-x-2">
              <button
                onClick={handleBackToPassengersOut}
                className="flex-1 py-2 px-3 bg-gray-100 text-gray-700 text-lg font-semibold rounded-lg hover:bg-gray-200 transition-colors border border-gray-300"
              >
                ← Back
              </button>
              <button
                onClick={handleDepart}
                disabled={isProcessing || !canDepart}
                className={`flex-1 py-2 px-3 text-lg font-semibold text-white rounded-lg transition-colors ${
                  waypoint?.endStep 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-green-600 hover:bg-green-700'
                } disabled:bg-gray-400 ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isProcessing ? 'Processing...' : (waypoint?.endStep ? 'Complete Route' : 'Depart')}
              </button>
          </div>

            {waypoint?.breakStep && breakTimeRemaining > 0 && (
              <div className="text-sm text-orange-600 text-center mt-1">
                Break: {Math.ceil(breakTimeRemaining / 60)} min remaining
          </div>
        )}
          </div>
        )}
    </div>
  );
};

export default FixedCurrentWaypoint;
